import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Languages, X } from "lucide-react";

export default function Traductions() {
  const { t, lang } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [traductions, setTraductions] = useState([]);
  const onlyObsolete = searchParams.get("obsolete") === "1";

  useEffect(() => {
    api.get("/traductions").then((res) => setTraductions(res.data));
  }, []);

  const visibleTraductions = onlyObsolete ? traductions.filter((tr) => tr.Obsolete) : traductions;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="traductions-view">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.translations")}</h1>
      {onlyObsolete && (
        <div className="flex items-center gap-2 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-lg px-3 py-2 w-fit" data-testid="obsolete-filter-chip">
          {lang === "fr" ? "Filtre : traductions obsolètes" : "Filter: obsolete translations"}
          <button onClick={() => setSearchParams({})} data-testid="btn-clear-obsolete-filter">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {visibleTraductions.length === 0 ? (
        <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-10 flex flex-col items-center text-center gap-3 text-slate-500" data-testid="traductions-empty">
          <Languages className="w-8 h-8" />
          <p className="max-w-md text-sm">
            {lang === "fr"
              ? "Aucune traduction pour le moment. Ouvrez un protocole et cliquez sur « Traduire en anglais » pour lancer la traduction IA (Claude Sonnet 4.6) basée sur le Glossaire_HSE."
              : "No translation yet. Open a protocol and click \"Translate to English\" to run the AI translation (Claude Sonnet 4.6) based on Glossaire_HSE."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleTraductions.map((tr) => (
            <Link
              key={tr.Traduction_ID}
              to={`/protocole/${tr.Protocol_ID}`}
              className="flex items-center justify-between border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
              data-testid={`traduction-row-${tr.Traduction_ID}`}
            >
              <div>
                <p className="font-semibold text-slate-800 dark:text-slate-200">{tr.Protocol_Titre || tr.Protocol_ID}</p>
                <p className="text-xs text-slate-500">
                  {tr.Protocol_ID} · {tr.Langue} · {tr.Moteur_IA} · {new Date(tr.Date_Maj).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {tr.Obsolete && (
                  <span className="text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-full px-2 py-0.5" data-testid={`obsolete-badge-${tr.Traduction_ID}`}>
                    {t("common.obsolete")}
                  </span>
                )}
                <StatusBadge status={tr.Protocol_Statut} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
